<template>
  <div
    class="uk-grid uk-margin-bottom"
  >
    <portal to="entry_header">
      <h1>Competitor Details</h1>
    </portal>
    <div
      v-if="!isStudent"
      class="uk-width-1-1"
    >
      <div
        class="uk-form-row student-details-warning"
        v-html="$t('events.enter_competitor_information_not_your_own')"
      />
    </div>
    <div class="uk-width-1-1 uk-width-medium-1-3 uk-grid-margin">
      <SelectRow
        v-model="form.salutation"
        name="salutation"
        :label="$t('events.entry_salutation_label')"
        :error-message="formError('salutation', 'required')"
        :disabled="disabled"
        :options="saluations"
        @input="emitFormInput"
      />
    </div>
    <div class="uk-width-1-1 uk-width-medium-1-3 uk-grid-margin">
      <input-row
        v-model="$v.form.first_name.$model"
        name="first_name"
        :label="$t('events.entry_first_name_label')"
        :error-message="formError('first_name', 'required')"
        :disabled="disabled"
        @input="emitFormInput"
      />
    </div>
    <div class="uk-width-1-1 uk-width-medium-1-3 uk-grid-margin">
      <input-row
        v-model="$v.form.last_name.$model"
        name="last_name"
        :label="$t('events.entry_last_name_label')"
        :error-message="formError('last_name', 'required')"
        :disabled="disabled"
        @input="emitFormInput"
      />
    </div>
    <div class="uk-width-1-1 uk-grid-margin">
      <input-row
        v-model="form.mobile"
        name="mobile"
        :label="$t('events.entry_mobile_label')"
        placeholder="+1 000 000-0000"
        :disabled="disabled"
        @input="emitFormInput"
      />
      <div class="uk-form-help-block">
        Optional in case we need to contact you quickly about your entry.
      </div>
    </div>

    <div class="uk-width-1-1 uk-grid-margin">
      <date-picker
        v-model="$v.form.dob.$model"
        :label="$t('events.entry_dob')"
        input-class="uk-width-1-1"
        :input-attrs="{max: maxDob}"
        :error-message="formError('dob', 'required')"
        :use-text-input="true"
        :disabled="disabled"
        @input="emitFormInput"
      >
        <template #label>
          <span style="font-weight: normal">Only visible to you.</span>
        </template>
      </date-picker>
      <div
        v-if="entryDraft.data.registrar_role !== 'student'"
        class="uk-form-help-block uk-text-danger"
      >
        Please enter the date of birth for the person you are registering, not your own.
      </div>
    </div>
    <div class="uk-width-1-1 uk-grid-margin">
      <Radios
        v-model="$v.form.gender.$model"
        name="gender"
        :input-values="genderRadioValues"
        :label="$t('events.entry_gender')"
        :error-message="formError('gender', 'required')"
        :disabled="disabled"
        @input="emitFormInput"
      />
    </div>
    <div
      ref="countrySelector"
      class="uk-width-1-1 uk-grid-margin"
    >
      <SelectRow
        v-model="$v.form.student_country.$model"
        :options="countryOptions"
        name="student_country"
        :label="$t('events.entry_country_label')"
        :error-message="formError('student_country', 'required')"
        :disabled="disabled"
        @input="emitFormInput"
      />
    </div>

    <div class="uk-width-1-2 uk-grid-margin">
      <input-row
        v-model="form.dan_id"
        name="dan_id"
        :label="$t('events.entry_dan_id_label')"
        :disabled="disabled"
        @input="emitFormInput"
      />
    </div>
    <div class="uk-width-1-2 uk-grid-margin">
      <input-row
        v-model="form.gup_id"
        name="gup_id"
        :label="$t('events.entry_gup_id_label')"
        :disabled="disabled"
        @input="emitFormInput"
      />
    </div>
    <div
      v-if="formError('gup_id', 'required') || formError('dan_id', 'required')"
      class="uk-width-1-1"
    >
      <p class="uk-text-danger">
        {{ formError('gup_id', $t('events.gup_dan_error')) || formError('dan_id', $t('events.gup_dan_error')) }}
      </p>
    </div>
    <div class="uk-width-1-1 uk-grid-margin">
      <SelectRow
        v-model="$v.form.rank_id.$model"
        :options="rankOptions"
        name="rank_id"
        :label="$t('events.entry_rank_label')"
        :error-message="formError('rank_id', 'required')"
        :disabled="disabled"
        @input="emitFormInput"
      />
    </div>

    <div class="uk-width-1-1 uk-grid-margin">
      <SelectRow
        v-model="$v.form.division_id.$model"
        :options="divisionOptions"
        name="division"
        :label="$t('events.entry_division')"
        :error-message="formError('division_id', 'required')"
        css-class="vs__dropdown--blank-options"
        :disabled="disabledDivision"
        @input="emitFormInput"
      >
        <template #label>
          <label class="uk-flex uk-flex-middle"><input
            v-model="filterEligibleDivisions"
            type="checkbox"
            class="uk-margin-small-right"
          > {{ $t('events.only_show_eligible_divisions') }}</label>
        </template>
        <template #above>
          <div
            v-if="event.meta.divisions && event.meta.divisions.description"
            v-html="event.meta.divisions.description"
          />
        </template>
        <template #option="slotProps">
          <div class="divOption">
            <h3 class="divOption__title">
              {{ slotProps.option.label }}
            </h3>
            <div class="divOption__meta">
              <div class="">
                <div v-if="slotProps.option.division.data.recommended_price && !slotProps.option.division.data.fixed_price">
                  <span>{{ event.meta.checkout.label_recommended_price }} ${{ slotProps.option.division.data.recommended_price }}</span>
                </div>
                <span v-if="slotProps.option.division.data.is_free">FREE</span>
                <span v-else>{{ event.meta.checkout.label_minimum_price }} ${{ slotProps.option.division.data.minimum_price }}</span>
              </div>
              <div
                class=""
              >
                {{ $t('events.age_range_label') }} <span v-if="slotProps.option.division.data.min_age >= 0 && slotProps.option.division.data.max_age > 0">
                  {{ slotProps.option.division.data.min_age }}–{{ slotProps.option.division.data.max_age }}
                </span>
                <span v-else-if="slotProps.option.division.data.min_age > 0 && slotProps.option.division.data.max_age === 0">
                  {{ slotProps.option.division.data.min_age }}+
                </span>
                <span v-else>
                  any
                </span>
              </div>
              <div class="">
                {{ $t('events.division_type_label') }} {{
                  slotProps.option.division.data.type
                }}
              </div>
              <div
                class=""
              >
                {{ $t('events.entry_gender') }} {{ slotProps.option.division.data.gender }}
              </div>
            </div>
          </div>
        </template>
      </SelectRow>

      <div
        v-if="divisionRequiresPayment"
        class="uk-alert uk-alert-warning"
      >
        You have already paid for this entry but the paid amount is lower than the minimum price of this division. We will automatically refund this entry and
        you will need to pay again to validate the entry.
      </div>

      <div
        v-if="showDivisionWarning"
        class="uk-alert uk-alert-warning"
      >
        There are no divisions matching your age group and gender.
      </div>
    </div>

    <div
      v-if="division && division.data.type.toLowerCase() === 'hyung'"
      class="uk-width-1-1 uk-grid-margin"
    >
      <SelectRow
        v-model="form.hyung"
        :options="hyungOptions"
        name="hyung"
        :label="$t('events.hyung_name_label')"
        :error-message="formError('hyung', 'required')"
        :disabled="disabled"
        @input="emitFormInput"
      />
    </div>

    <div
      v-if="!event.data.force_custom_instructor"
      class="uk-width-1-1 uk-grid-margin"
    >
      <SelectRow
        v-model="form.instructor_id"
        :options="instructorOptions"
        name="instructor_id"
        :label="$t('events.instructor_name_label')"
        :error-message="formError('instructor_id', 'required')"
        :disabled="disabled"
        @input="emitFormInput"
      />
    </div>

    <div
      v-if="form.instructor_id === '-1'"
      class="uk-width-1-1 uk-grid-margin"
    >
      <input-row
        v-model="$v.form.instructor.$model"
        name="instructor"
        :label="$t('events.entry_custom_instructor_label')"
        :error-message="formError('instructor', 'required')"
        :disabled="disabled"
        @input="emitFormInput"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import form from "../form/form-wrapper.vue";
import formRow from "../form/form-row.vue";
import input from "../form/input-row.vue";
import formError from "../form/form-error.vue";
import datePicker from "../form/date-picker.vue";
import SelectRow from "../form/select-row.vue";
import Radios from "../form/input-radios.vue";
import DivisionModel from "../../models/DivisionModel";
import { DateTime as LuxonDatetime } from "luxon";
import { validationMixin } from 'vuelidate';
import { required, not, requiredIf } from 'vuelidate/lib/validators';
import FormStep from "./FormStep";
import EventEntryModel, {create as createEntry} from "../../models/EventEntryModel";
import EventModel from "../../models/EventModel";

@Component({
  components: {
    'form-wrapper': form,
    'form-row': formRow,
    'input-row': input,
    'form-error': formError,
    'date-picker': datePicker,
    SelectRow,
    Radios,
  },
  mixins: [validationMixin],
  validations: {
    form: {
      salutation: {
        required,
      },
      first_name: {
        required
      },
      last_name: {
        required
      },
      // mobile: {
      //   required
      // },
      dob: {
        required
      },
      gender: {
        required
      },
      rank_id: {
        required
      },
      division_id: {
        required,
        not: not((model: string) => {
          return model === '';
        })
      },
      hyung: {
        requiredIfDivisionTypeHyung: function (this: StudentDetailsStep, value: string | null) {
          const isHyung = this.division && this.division.data.type.toLowerCase() === 'hyung';

          return (isHyung && value !== null && value.length > 0) || !isHyung;
        }
      },
      // dan_id: {
      //   required: requiredIf(function (this: StudentDetailsStep) {
      //     return !this.form.gup_id;
      //   })
      // },
      // gup_id: {
      //   required: requiredIf(function (this: StudentDetailsStep) {
      //     return !this.form.dan_id;
      //   })
      // },
      instructor_id: {
        required: requiredIf(function (this: StudentDetailsStep) {
          return !this.form.instructor;
        })
      },
      instructor: {
        required: requiredIf(function (this: StudentDetailsStep) {
          return !this.form.instructor_id || this.form.instructor_id === "-1";
        })
      },
      student_country: {
        required
      }
    }
  }
})
export default class StudentDetailsStep extends FormStep {
  form: StudentDetails = {
    salutation: '',
    first_name: '',
    last_name: '',
    dob: '',
    gender: '',
    division_id: '',
    hyung: '',
    dan_id: '',
    gup_id: '',
    rank_id: '',
    email: '',
    mobile: '',
    instructor: '',
    instructor_id: '',
    student_country: '',
  };

  filterEligibleDivisions = true;

  @Prop() readonly entryDraft!: EventEntryModel;

  @Prop() readonly entry!: EventEntryModel;

  @Prop() readonly eventId!: number;

  @Prop() readonly value!: StudentDetails;

  data(): any {
    const form: StudentDetails = JSON.parse(JSON.stringify(this.value));
    return {
      form
    }
  }

  created(): void {
    if (this.event.data.force_custom_instructor) {
      this.form.instructor_id = '-1';
    }

    if (this.form.student_country === '') {
      this.form.student_country = 'US';
    }
    this.$store.dispatch('countries/fetchOptions').then(() => {
      if (this.form.student_country === '' && this.$store.getters['countries/guess']) {
        Vue.set(this.form, 'student_country', this.$store.getters['countries/guess'].code);
        this.emitFormInput();
      }
    });
  }

  get event(): EventModel {
    return this.$store.getters['events/byId'](this.eventId);
  }

  get instructorOptions(): {label: string, value: string}[] {
    const instructors = this.$store.getters['modules/getModuleContext']('events-submissions').instructors;
    return [{label: 'My instructor is not listed', value: "-1"}, ...instructors.map((r: any) => {
      return {label: `${r.last_name} ${r.first_name} ${r.salutation} — ${r.school}`, value: ''+r.id};
    })];
  }

  get rankOptions(): {label: string, value: string}[] {
    const ranks = this.$store.getters['modules/getModuleContext']('events-submissions').ranks;
    return ranks.map((r: any) => {
      return {label: r.title, value: ''+r.id};
    });
  }

  get hyungOptions(): SelectOption[] {
    return [
      {label: "Adaptive Hyung or Techniques", value: "Adaptive Hyung or Techniques"},
      {label: "Pal Gwe (Techiques)", value: "Pal Gwe (Techiques)"},
      {label: "Warrior Tot Hyung or Techniques", value: "Warrior Tot Hyung or Techniques"},
      {label: "Ki Cho Hyung Il Bu", value: "Ki Cho Hyung Il Bu"},
      {label: "Ki Cho Hyung Ee Bu", value: "Ki Cho Hyung Ee Bu"},
      {label: "Ki Chi Hyung Sam Bu", value: "Ki Chi Hyung Sam Bu"},
      {label: "Pyong Ahn Cho Dan", value: "Pyong Ahn Cho Dan"},
      {label: "Pyong Ahn Ee Dan", value: "Pyong Ahn Ee Dan"},
      {label: "Pyong Ahn Sam Dan", value: "Pyong Ahn Sam Dan"},
      {label: "Pyong Ahn Sa Dan", value: "Pyong Ahn Sa Dan"},
      {label: "Pyong Ahn O Dan", value: "Pyong Ahn O Dan"},
      {label: "Chil Sung Il Ro Hyung", value: "Chil Sung Il Ro Hyung"},
      {label: "Chil Sung Ee Ro Hyung", value: "Chil Sung Ee Ro Hyung"},
      {label: "Chil Sung Sam Ro Hyung", value: "Chil Sung Sam Ro Hyung"},
      {label: "Chil Sung Sa Ro Hyung", value: "Chil Sung Sa Ro Hyung"},
      {label: "Chil Sung O Ro Hyung", value: "Chil Sung O Ro Hyung"},
      {label: "Chil Sung Yuk Ro Hyung", value: "Chil Sung Yuk Ro Hyung"},
      {label: "Chil Sung Chil Ro Hyung", value: "Chil Sung Chil Ro Hyung"},
      {label: "Yuk Ro Cho Dan - Du Moon", value: "Yuk Ro Cho Dan - Du Moon"},
      {label: "Yuk Ro Ee Dan - Joong Jul", value: "Yuk Ro Ee Dan - Joong Jul"},
      {label: "Yuk Ro Sam Dan - Po Wol", value: "Yuk Ro Sam Dan - Po Wol"},
      {label: "Yuk Ro Sa Dan - Yang Pyun", value: "Yuk Ro Sa Dan - Yang Pyun"},
      {label: "Yuk Ro O Dan - Sal Chu", value: "Yuk Ro O Dan - Sal Chu"},
      {label: "Yuk Ro Yuk Dan - Choong Ro", value: "Yuk Ro Yuk Dan - Choong Ro"},
      {label: "Nai Han Ji Cho Dan", value: "Nai Han Ji Cho Dan"},
      {label: "Nai Han Ja Ee Dan", value: "Nai Han Ja Ee Dan"},
      {label: "Nai Han Ji Sam Dan", value: "Nai Han Ji Sam Dan"},
      {label: "Passai", value: "Passai"},
      {label: "Jin Do", value: "Jin Do"},
      {label: "Ro Hai", value: "Ro Hai"},
      {label: "Kong San Koon", value: "Kong San Koon"},
      {label: "Wang Shu", value: "Wang Shu"},
      {label: "Sip Soo", value: "Sip Soo"},
      {label: "Sei Shan", value: "Sei Shan"},
      {label: "Ji On", value: "Ji On"},
      {label: "O Sip Sa Bo", value: "O Sip Sa Bo"},
      {label: "Hwa Sun", value: "Hwa Sun"},
    ];
  }

  get maxDob(): string {
    return <string>LuxonDatetime.local().toISODate();
  }

  get showAllDivisions(): boolean {
    return !this.filterEligibleDivisions;
  }

  get division(): DivisionModel {
    return this.$store.getters['events/divisions/getById'](parseInt(this.form.division_id));
  }

  get showDivisionWarning(): boolean {

    if (this.form.dob.length === 0 || this.form.gender === '') {
      return false;
    }

    const entry = createEntry();
    entry.data.dob = this.form.dob;

    const eligibleCriteria = {
      age: entry.getAge(),
      gender: this.form.gender
    };
    return this.$store.getters['events/divisions/getEventDivisions'](this.eventId).filter((a: DivisionModel) => a.isEligible(eligibleCriteria)).length === 0;
  }

  get saluations(): SelectOption[] {
    let options = this.$store.getters['modules/getModuleContext']('events-submissions').salutations.map((a: string) => {
      return {label: a, value: a};
    });

    return options;
  }

  get divisionOptions(): { value: string, label: string, disabled: boolean, division: DivisionModel }[] {
    const divisions: DivisionModel[] = this.$store.getters['events/divisions/getEventDivisions'](this.eventId);
    const options = [];

    const entry = createEntry();
    entry.data.dob = this.form.dob;

    const eligibleCriteria = {
      age: entry.getAge(),
      gender: this.form.gender
    };

    for (const div of divisions) {
      if (div.data.id === this.event.data.spectator_division_id) {
        continue;
      }

      if (div.isEligible(eligibleCriteria) || this.showAllDivisions) {
        options.push({
          value: "" + div.data.id,
          label: div.getTitleLabel(),
          disabled: !div.isEligible(eligibleCriteria),
          division: div
        });
      }

      if (!div.isEligible(eligibleCriteria) && div.data.id === parseInt(this.form.division_id)) {
        this.form.division_id = ''; // Drop selected division if not eligible
      }
    }

    return options;
  }

  get divisionRequiresPayment(): boolean {
    if (this.entry.isPaid() && this.entry.order_item && this.division && this.entry.data.division_id !== this.entryDraft.data.division_id) {
      if (this.entry.order_item.data.value < this.division.data.minimum_price) {
        return true;
      }
    }
    return false;
  }

  get genderRadioValues(): Record<string, string> {
    const genders = this.$store.getters['modules/getModuleContext']('events-submissions')?.entry_genders;
    const options: Record<string, string> = {};
    for (const i in genders) {
      options[genders[i]] = genders[i].charAt(0).toUpperCase() + genders[i].slice(1);
    }
    return options;
  }

  get countryOptions(): { value: string, label: string}[] {
    const countries = this.$store.getters['countries/options'];
    const options = [];
    for (const i in countries) {
      options.push({
        value: i,
        label: countries[i],
      })
    }

    return options;
  }

  get isStudent(): boolean {
    return this.entryDraft.data.registrar_role === 'student';
  }

  @Watch('form.division_id', {immediate: true})
  clearHyungOnNonHyungDivisions(nV: string): void {
    const div: DivisionModel = this.$store.getters['events/divisions/getById'](nV);
    if (div && div.data.type !== 'Hyung') {
      this.form.hyung = '';
      this.emitFormInput();
    }
  }

  @Watch('form.instructor_id')
  clearCustomInstructor(nV: string): void {
    if (nV !== '-1') {
      this.form.instructor = '';
      this.emitFormInput();
    }
  }

  @Watch('value', {immediate: true, deep: true})
  valuePropUpdated(v: StudentDetails): void {
    this.form = JSON.parse(JSON.stringify(v));
  }

  emitFormInput(): void {
    this.$emit('input', this.form);
  }
}
</script>

<style scoped>
.student-details-warning {
  text-align: center;
  font-weight: bold;
  background: #f3e7b1;
  padding: 15px;
}
</style>
